import React from 'react';
import { Link } from 'react-router-dom';
import { usePageSEO } from '../hooks/usePageSEO';
import { breadcrumbSchema } from '../utils/structuredData';
import { SparklesIcon, CameraIcon, ArrowRightIcon, ExternalLinkIcon, SearchIcon } from 'lucide-react';
export function AppsPage() {
  usePageSEO({
    canonical: '/apps',
    keywords: 'Shopify apps, FlowBoost app, AI Photoshoot app, SEO Suite app, Shopify app development',
    jsonLd: breadcrumbSchema([{
      name: 'Home',
      path: '/'
    }, {
      name: 'Apps',
      path: '/apps'
    }]),
    title: 'Apps | Design Flow Digitals',
    description: 'Explore Shopify apps by Design Flow Digitals. FlowBoost for speed optimization and AI Photoshoot for stunning product photography powered by AI.'
  });
  return <div className="pt-32 pb-20" data-id="element-778">
      {/* Hero */}
      <section className="px-6 md:px-12 max-w-5xl mx-auto text-center mb-24" data-id="element-779">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card mb-8 border-[#D4AF37]/30" data-id="element-780">
          <SparklesIcon className="w-4 h-4 text-[#D4AF37]" data-id="element-781" />
          <span className="text-sm font-medium text-[#F3E5AB]" data-id="element-782">
            Shopify Apps
          </span>
        </div>
        <h1 className="text-5xl md:text-7xl font-bold mb-8" data-id="element-783">
          Smart Tools for Growth
        </h1>
        <p className="text-xl md:text-2xl text-gray-400 leading-relaxed max-w-3xl mx-auto" data-id="element-784">
          Powerful Shopify apps designed to improve store performance, automate
          workflows, and increase sales.
        </p>
      </section>

      {/* Categories Intro */}
      <section className="max-w-5xl mx-auto px-6 mb-20" data-id="element-785">
        <div className="flex flex-wrap justify-center gap-4" data-id="element-786">
          {['Conversion tools', 'Analytics tools', 'Automation apps', 'AI-powered tools'].map((cat, i) => <span key={i} className="px-6 py-3 rounded-full glass-card border-white/10 text-gray-300 font-medium" data-id="element-787">
              {cat}
            </span>)}
        </div>
      </section>

      {/* Featured Apps */}
      <section className="section-padding bg-black border-y border-white/5" data-id="element-788">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-789">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-790">Featured Apps</h2>
          <p className="text-xl text-gray-400" data-id="element-791">
            Discover our top-rated applications built for modern merchants.
          </p>
        </div>

        <div className="flex flex-col gap-12 max-w-5xl mx-auto" data-id="element-792">
          {/* SEO Suite App (Latest Release Highlight) */}
          <div className="glass-card p-8 md:p-12 border-emerald-500/30 bg-gradient-to-br from-emerald-900/10 to-transparent relative overflow-hidden group" data-id="element-793">
            <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/10 rounded-full blur-[80px] pointer-events-none" data-id="element-794" />

            <div className="flex flex-col md:flex-row gap-10 items-start relative z-10" data-id="element-795">
              <div className="w-24 h-24 shrink-0 rounded-2xl bg-emerald-500/20 flex items-center justify-center border border-emerald-500/30 shadow-[0_0_30px_rgba(16,185,129,0.2)]" data-id="element-796">
                <SearchIcon className="w-12 h-12 text-emerald-400" data-id="element-797" />
              </div>

              <div className="flex-1" data-id="element-798">
                <div className="flex items-center gap-3 mb-3" data-id="element-799">
                  <h3 className="text-3xl font-bold text-white" data-id="element-800">SEO Suite</h3>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-bold uppercase tracking-wider" data-id="element-801">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" data-id="element-802" />{' '}
                    Latest Release
                  </span>
                </div>

                <p className="text-emerald-300 font-medium mb-4" data-id="element-803">
                  Free plan available
                </p>

                <p className="text-xl text-gray-300 mb-6 leading-relaxed" data-id="element-804">
                  The all-in-one SEO solution for Shopify. Automate your
                  technical SEO, optimize images, and dominate search rankings.
                </p>

                <ul className="space-y-3 mb-8" data-id="element-805">
                  {['Smart meta tags generation using AI', 'Automated image optimization & ALT tags', 'Technical SEO fixes and broken link management'].map((feature, i) => <li key={i} className="flex items-start gap-3 text-gray-400" data-id="element-806">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-2 shrink-0" data-id="element-807" />
                      {feature}
                    </li>)}
                </ul>

                <div className="flex flex-wrap gap-4" data-id="element-808">
                  <Link to="/seo-suite" className="px-8 py-3 rounded-full font-bold text-white bg-emerald-600 hover:bg-emerald-500 transition-colors shadow-[0_0_20px_rgba(16,185,129,0.3)]" data-id="element-809">
                    View Details
                  </Link>
                  <a href="https://apps.shopify.com/seo-suite" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-8 py-3 rounded-full font-bold text-white border border-white/20 hover:bg-white/5 transition-colors" data-id="element-810">
                    Install on Shopify <ExternalLinkIcon className="w-4 h-4" data-id="element-811" />
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* AI Product Photoshoot App */}
          <div className="glass-card p-8 md:p-12 border-purple-500/30 bg-gradient-to-br from-purple-900/10 to-transparent relative overflow-hidden group" data-id="element-812">
            <div className="absolute top-0 right-0 w-64 h-64 bg-purple-500/10 rounded-full blur-[80px] pointer-events-none" data-id="element-813" />

            <div className="flex flex-col md:flex-row gap-10 items-start relative z-10" data-id="element-814">
              <div className="w-24 h-24 shrink-0 rounded-2xl bg-purple-500/20 flex items-center justify-center border border-purple-500/30 shadow-[0_0_30px_rgba(168,85,247,0.2)]" data-id="element-815">
                <CameraIcon className="w-12 h-12 text-purple-400" data-id="element-816" />
              </div>

              <div className="flex-1" data-id="element-817">
                <div className="flex items-center gap-3 mb-3" data-id="element-818">
                  <h3 className="text-3xl font-bold text-white" data-id="element-819">
                    AI Product Photoshoot - DFD
                  </h3>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-500/10 border border-green-500/20 text-green-400 text-xs font-bold uppercase tracking-wider" data-id="element-820">
                    <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" data-id="element-821" />{' '}
                    Live
                  </span>
                </div>

                <p className="text-purple-300 font-medium mb-4" data-id="element-822">
                  Free plan available
                </p>

                <p className="text-xl text-gray-300 mb-6 leading-relaxed" data-id="element-823">
                  Generate AI lifestyle images using your existing store product
                  photos. No studio required.
                </p>

                <ul className="space-y-3 mb-8" data-id="element-824">
                  {['AI-powered image generation from your catalog', 'Publish generated images directly to your products', '20+ product categories for image generation'].map((feature, i) => <li key={i} className="flex items-start gap-3 text-gray-400" data-id="element-825">
                      <div className="w-1.5 h-1.5 rounded-full bg-purple-500 mt-2 shrink-0" data-id="element-826" />
                      {feature}
                    </li>)}
                </ul>

                <div className="flex flex-wrap gap-4" data-id="element-827">
                  <Link to="/ai-photoshoot" className="px-8 py-3 rounded-full font-bold text-white bg-purple-600 hover:bg-purple-500 transition-colors shadow-[0_0_20px_rgba(147,51,234,0.3)]" data-id="element-828">
                    View Details
                  </Link>
                  <a href="https://apps.shopify.com/ai-product-photoshoot-dfd" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-8 py-3 rounded-full font-bold text-white border border-white/20 hover:bg-white/5 transition-colors" data-id="element-829">
                    Install on Shopify <ExternalLinkIcon className="w-4 h-4" data-id="element-830" />
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* FlowBoost App */}
          <div className="glass-card p-8 md:p-12 border-blue-500/20 bg-gradient-to-br from-blue-900/5 to-transparent relative overflow-hidden" data-id="element-831">
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/5 rounded-full blur-[80px] pointer-events-none" data-id="element-832" />

            <div className="flex flex-col md:flex-row gap-10 items-start relative z-10" data-id="element-833">
              <div className="w-20 h-20 shrink-0 rounded-2xl bg-blue-500/10 flex items-center justify-center border border-blue-500/20" data-id="element-834">
                <SparklesIcon className="w-10 h-10 text-blue-400" data-id="element-835" />
              </div>

              <div className="flex-1" data-id="element-836">
                <div className="flex items-center gap-3 mb-3" data-id="element-837">
                  <h3 className="text-2xl font-bold text-white" data-id="element-838">FlowBoost</h3>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/20 text-[#D4AF37] text-xs font-bold uppercase tracking-wider" data-id="element-839">
                    <span className="w-2 h-2 rounded-full bg-[#D4AF37]" data-id="element-840" />{' '}
                    Coming Soon
                  </span>
                </div>
                <p className="text-lg text-gray-300 mb-6 leading-relaxed" data-id="element-841">
                  Boost your Shopify store's conversion rate with intelligent
                  upsell automation.
                </p>

                <ul className="space-y-3 mb-8" data-id="element-842">
                  {['AI product recommendations', 'Smart cart upsells', 'Analytics dashboard'].map((feature, i) => <li key={i} className="flex items-start gap-3 text-gray-400" data-id="element-843">
                      <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2 shrink-0" data-id="element-844" />
                      {feature}
                    </li>)}
                </ul>

                <Link to="/flowboost" className="inline-flex items-center gap-2 px-8 py-3 rounded-full font-bold text-white border border-white/20 hover:bg-white/5 transition-colors" data-id="element-845">
                  View Details <ArrowRightIcon className="w-4 h-4" data-id="element-846" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 text-center" data-id="element-847">
        <h2 className="text-3xl md:text-5xl font-bold mb-6" data-id="element-848">
          Have an app idea?
        </h2>
        <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto" data-id="element-849">
          We build custom public and private Shopify apps tailored to your
          specific business needs.
        </p>
        <Link to="/contact" className="inline-flex items-center justify-center gap-2 bg-gradient-gold text-black px-10 py-4 rounded-full font-bold text-lg gold-glow-hover transition-all duration-300 hover:scale-105" data-id="element-850">
          Let's build it together <ArrowRightIcon className="w-5 h-5" data-id="element-851" />
        </Link>
      </section>
    </div>;
}